import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getShopifyCollections } from "@redotech/redo-merchant-app-common/client/shopify";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import {
  convertShopifyIdToGid,
  ShopifyIdType,
} from "@redotech/shopify-util/id";
import { isDef } from "@redotech/util/checks";
import { memo, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";

export const CollectionSelectionModal = memo(function CollectionSelectionModal({
  selectedCollections,
  onCollectionsSelected,
  onClose,
}: {
  selectedCollections: string[];
  onCollectionsSelected: (collections: string[]) => void;
  onClose: () => void;
}) {
  const merchantClient = useRequiredContext(RedoMerchantClientContext);

  const [localSelectedCollections, setLocalSelectedCollections] =
    useState<string[]>(selectedCollections);
  const [searchString, setSearchString] = useState("");

  const debouncedSearch = useDebounce(searchString, 500);

  const collectionsLoad = useLoad(async () => {
    const collections = await getShopifyCollections(merchantClient, {
      searchString: debouncedSearch,
    });
    return collections.map((collection) => ({
      id: convertShopifyIdToGid(collection.id, ShopifyIdType.Collection),
      title: collection.name,
    }));
  }, [debouncedSearch, merchantClient]);

  const collectionOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return (
      collectionsLoad.value
        ?.filter(isDef)
        .map((collection) => ({
          type: "badge",
          id: collection.id,
          value: collection.id,
          badge: { text: collection.title, color: "gray" },
        })) ?? []
    );
  }, [collectionsLoad.value]);

  const selectedOptions = useMemo(() => {
    return collectionOptions.filter((option) =>
      localSelectedCollections.includes(option.value),
    );
  }, [collectionOptions, localSelectedCollections]);

  const handleSelectionChange = useHandler(
    (items: BadgeRedoListItem<string>[]) => {
      setLocalSelectedCollections(items.map((item) => item.value));
    },
  );

  const handleSave = useHandler(() => {
    onCollectionsSelected(localSelectedCollections);
  });

  return (
    <RedoModal
      modalSize="md"
      onModalCloseRequested={onClose}
      primaryButton={{ text: "Save", onClick: handleSave }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      title="Select Collections"
    >
      <Flex dir="column" gap="lg" p="md">
        <RedoTagsDropdownInput<string>
          label="Collections"
          loading={collectionsLoad.pending}
          options={collectionOptions}
          placeholder="Select collections"
          searchPlaceholder="Search for collections"
          searchString={searchString}
          selectedOptions={selectedOptions}
          setSearchString={setSearchString}
          setSelectedOptions={handleSelectionChange}
          size="xs"
        />
      </Flex>
    </RedoModal>
  );
});
